import type { ShowConsultation } from '@/components/PatientPageV2/components/ConsultationTabSections/ShowConsultation';
import { useAppStore } from '@/state-management';
import { TelecomWaitingRoomEntryStatus } from '@montugroup/pms-api-contracts';
import { Box, Skeleton, Stack, useTheme } from '@mui/material';

import { ConsultCharge } from '@/components/PatientPageV2/components/ConsultationTabSections/ConsultCharge/ConsultCharge';
import { FF_ENABLE_INITIAL_DR_PAYMENT } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks/flags/useFeatureFlags';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { Alert } from '@montugroup/design-system';
import { useMemo, useState } from 'react';
import type { PrescriptionAssistantToolDetails } from '../components/PrescriptionAssistantTool/PrescriptionAssistantTool.types';
import { AmazonConnectCallBar } from './AmazonConnectCallBar/AmazonConnectCallBar';
import { ConsultActions } from './ConsultActions/ConsultActions';
import { ConsultHistory } from './ConsultHistory/ConsultHistory';
import { ConsultNotes } from './ConsultNotes/ConsultNotes';
import { useSaveScheduledConsult } from './hooks/useSaveScheduledConsult';
import { useScheduledConsult } from './hooks/useScheduledConsult';
import { useScheduledConsultId } from './hooks/useScheduledConsultId';
import { PatientCheck } from './PatientCheck/PatientCheck';
import { Prescriptions } from './Prescriptions/Prescriptions';
import { StyledActionsContainer } from './ScheduledConsult.styles';
import { Section } from './Section/Section';
import { SectionIds } from './types';

export interface ScheduledConsultProps {
  prescriptionDetails: PrescriptionAssistantToolDetails;
  isConcessionPatient: boolean;
  isPatientActive: boolean;
  setConsultationStarted: (consultationStarted: boolean) => void;
  consultationStarted?: boolean;
  onPrescriptionErrorClosed: () => void;
}

export const ScheduledConsult = ({
  prescriptionDetails,
  isConcessionPatient,
  isPatientActive,
  consultationStarted,
  setConsultationStarted,
  onPrescriptionErrorClosed,
}: ScheduledConsultProps) => {
  const theme = useTheme();
  const scheduledConsultId = useScheduledConsultId();
  const store = useAppStore();
  const patientId = store.getPatientId();
  const notes = store.guidedDoctorConsult.fields.notes ?? '';
  const { flags } = useFeatureFlags();
  const ffEnableInitialDrPayment = flags[FF_ENABLE_INITIAL_DR_PAYMENT];

  const [isProcessingAction, setIsProcessingAction] = useState(false);

  const { scheduledConsult, isPending, isError, isNotFound } = useScheduledConsult({
    patientId: patientId ?? undefined,
    scheduledConsultId,
  });

  const updatedConsultation: Consultation | undefined = useMemo(
    () =>
      scheduledConsult
        ? {
            ...scheduledConsult,
            notes,
          }
        : undefined,
    [scheduledConsult, notes],
  );

  const { saveConsult, submitCreatePrescription, isPostingPrescription, prescribeStateError } = useSaveScheduledConsult(
    {
      consultation: (updatedConsultation as ShowConsultation | undefined) ?? null,
      doctorId: prescriptionDetails.doctorId,
    },
  );

  if (isPending) {
    return (
      <Box data-testid="scheduled-consult-skeleton">
        <Skeleton variant="rounded" height={70} sx={{ mb: theme.spacing(6) }} />
        <Stack spacing={0.5}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton variant="rounded" height={46} key={index} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (isError || isNotFound || !scheduledConsult) {
    return (
      <Alert severity="error">
        {isNotFound || !scheduledConsult
          ? "We couldn't find this consultation."
          : 'Something went wrong while loading the consultation.'}
      </Alert>
    );
  }

  return (
    <Box data-testid="scheduled-consult">
      <AmazonConnectCallBar
        consultationStarted={consultationStarted}
        setConsultationStarted={setConsultationStarted}
        waitingRoomStatus={scheduledConsult?.waiting_room_status ?? TelecomWaitingRoomEntryStatus.Created}
        consultationId={scheduledConsultId ?? -1}
        consultationCode={scheduledConsult?.consultation_code}
      />

      <Box>
        <Section title="Patient Check" sectionId={SectionIds.PATIENT_CHECK}>
          <PatientCheck />
        </Section>

        <Section title="Consult History" sectionId={SectionIds.CONSULT_HISTORY}>
          {patientId && <ConsultHistory patientId={patientId} scheduledConsultId={scheduledConsultId} />}
        </Section>

        <Section title="Consult Notes" sectionId={SectionIds.CONSULT_NOTES}>
          <ConsultNotes scheduledConsult={scheduledConsult} />
        </Section>

        <Section title="Prescriptions" sectionId={SectionIds.PRESCRIPTIONS}>
          <Prescriptions
            prescriptionDetails={prescriptionDetails}
            isPostingPrescription={isPostingPrescription}
            prescribeStateError={prescribeStateError}
            onPrescriptionErrorClosed={onPrescriptionErrorClosed}
            setIsProcessingAction={setIsProcessingAction}
            consultation={updatedConsultation ?? null}
            isConcessionPatient={isConcessionPatient}
            isPatientActive={isPatientActive}
            saveConsult={saveConsult}
          />
        </Section>

        {ffEnableInitialDrPayment && (
          <Box marginTop={theme.spacing(6)}>
            <ConsultCharge variant="doctor" consultation={scheduledConsult} isConcessionPatient={isConcessionPatient} />
          </Box>
        )}

        <StyledActionsContainer>
          <ConsultActions
            consultation={(updatedConsultation as ShowConsultation | undefined) ?? null}
            isConcessionPatient={isConcessionPatient}
            isPatientActive={isPatientActive}
            saveConsult={saveConsult}
            submitCreatePrescription={submitCreatePrescription}
            isProcessingAction={isProcessingAction}
          />
        </StyledActionsContainer>
      </Box>
    </Box>
  );
};
